import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { FleetMockStore } from '../mock/fleet-mock.store';
import { ExpenseCategory, ExpenseRecord, FleetVehicle, FLEET_STATUS_META, MaintenanceRecord } from '../mock/models/fleet.models';

type TabKey = 'resumen' | 'mantenimientos' | 'reparaciones' | 'gastos' | 'conductores' | 'documentos';

@Component({
  selector: 'app-vehicle-360-profile',
  templateUrl: './vehicle-360-profile.component.html',
  styleUrls: ['./vehicle-360-profile.component.css'],
})
export class Vehicle360ProfileComponent implements OnChanges, OnDestroy {
  @Input() vehicleId: string | null = null;
  @Input() open = false;
  @Output() closed = new EventEmitter<void>();

  vehicle?: FleetVehicle;
  activeTab: TabKey = 'resumen';

  readonly statusMeta = FLEET_STATUS_META;

  readonly tabs: { key: TabKey; label: string }[] = [
    { key: 'resumen', label: 'Resumen' },
    { key: 'mantenimientos', label: 'Mantenimientos' },
    { key: 'reparaciones', label: 'Reparaciones' },
    { key: 'gastos', label: 'Gastos' },
    { key: 'conductores', label: 'Conductores' },
    { key: 'documentos', label: 'Documentos' },
  ];

  private sub?: Subscription;
  private readonly store = inject(FleetMockStore);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['vehicleId']) {
      this.activeTab = 'resumen';
      this.sub?.unsubscribe();
      this.vehicle = undefined;
      if (this.vehicleId) {
        this.sub = this.store.vehicle$(this.vehicleId).subscribe((v) => (this.vehicle = v));
      }
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  setTab(tab: TabKey): void {
    this.activeTab = tab;
  }

  close(): void {
    this.closed.emit();
  }

  // ---- Estado / semáforo ----
  get statusPillClass(): string {
    switch (this.vehicle?.status_live) {
      case 'MOVING':
        return 'bg-emerald-500/10 text-emerald-500';
      case 'STOPPED':
        return 'bg-red-500/10 text-red-500';
      case 'IDLE':
        return 'bg-amber-500/10 text-amber-500';
      default:
        return 'bg-gray-500/10 text-gray-500';
    }
  }

  // ---- Mantenimientos ----
  get upcomingMaintenance(): MaintenanceRecord[] {
    return (this.vehicle?.maintenance ?? []).filter((m) => m.status === 'UPCOMING' || m.status === 'OVERDUE');
  }

  get historyMaintenance(): MaintenanceRecord[] {
    return (this.vehicle?.maintenance ?? []).filter((m) => m.status === 'DONE');
  }

  maintenanceCardClass(m: MaintenanceRecord): string {
    if (m.status === 'OVERDUE') {
      return 'border-red-500/40 bg-red-500/5';
    }
    if (m.status === 'UPCOMING') {
      return 'border-amber-500/40 bg-amber-500/5';
    }
    return 'border-gray-100 dark:border-white/10 bg-white dark:bg-white/5';
  }

  maintenanceBadgeClass(m: MaintenanceRecord): string {
    if (m.status === 'OVERDUE') {
      return 'bg-red-500/10 text-red-500';
    }
    if (m.status === 'UPCOMING') {
      return 'bg-amber-500/10 text-amber-500';
    }
    return 'bg-emerald-500/10 text-emerald-500';
  }

  maintenanceStatusLabel(m: MaintenanceRecord): string {
    switch (m.status) {
      case 'OVERDUE':
        return 'Vencido';
      case 'UPCOMING':
        return 'Próximo';
      default:
        return 'Realizado';
    }
  }

  // ---- Gastos ----
  get expensesTotal(): number {
    return (this.vehicle?.expenses ?? []).reduce((a, e) => a + (e.amount ?? 0), 0);
  }

  expenseCategoryLabel(cat: ExpenseCategory): string {
    const map: Record<ExpenseCategory, string> = {
      fuel: 'Combustible',
      maintenance: 'Mantenimiento',
      repair: 'Reparación',
      insurance: 'Seguro',
      toll: 'Peaje',
      other: 'Otro',
    };
    return map[cat] ?? cat;
  }

  expenseCategoryClass(cat: ExpenseCategory): string {
    const map: Record<ExpenseCategory, string> = {
      fuel: 'bg-sky-500/10 text-sky-500',
      maintenance: 'bg-emerald-500/10 text-emerald-500',
      repair: 'bg-amber-500/10 text-amber-500',
      insurance: 'bg-violet-500/10 text-violet-500',
      toll: 'bg-cyan-500/10 text-cyan-500',
      other: 'bg-gray-500/10 text-gray-500',
    };
    return map[cat] ?? 'bg-gray-500/10 text-gray-500';
  }

  trackByExpense(_: number, e: ExpenseRecord): string {
    return e.id;
  }

  // ---- Formato monetario (pesos colombianos: 1.200.000) ----
  cop(amount: number | undefined | null): string {
    const n = Math.round(Number(amount ?? 0));
    return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }
}
